import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Wallet, Mail, Lock, Eye, EyeOff, TrendingUp, Shield, MessageSquare, Target, Zap, Sparkles, BarChart3 } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

const PUNCH_LINES = [
  'Just say it. Chanakya handles the rest.',
  'Your CA is going to hate us.',
  'Know where every rupee goes.',
  'No spreadsheets. No stress.',
  'Financial clarity, finally.',
];

function BrandingPanel() {
  const [lineIdx, setLineIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setLineIdx(i => (i + 1) % PUNCH_LINES.length), 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="hidden lg:flex bg-[#0d0f13] relative overflow-hidden" style={{ minHeight: '100vh' }}>
      <style>{`
        @keyframes orb1 { 0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(30px,-20px) scale(1.1);} }
        @keyframes orb2 { 0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(-20px,25px) scale(0.9);} }
        @keyframes floatA { 0%,100%{transform:translateY(0px) rotate(0deg);}50%{transform:translateY(-12px) rotate(1deg);} }
        @keyframes floatB { 0%,100%{transform:translateY(0px) rotate(0deg);}50%{transform:translateY(10px) rotate(-1deg);} }
        @keyframes floatC { 0%,100%{transform:translateY(0px);}60%{transform:translateY(-8px);} }
        @keyframes fadeSlide { 0%{opacity:0;transform:translateY(8px);}100%{opacity:1;transform:translateY(0);} }
        @keyframes shimmerText { 0%{background-position:200% center}100%{background-position:-200% center} }
        .float-a { animation: floatA 4s ease-in-out infinite; }
        .float-b { animation: floatB 5.5s ease-in-out infinite 0.6s; }
        .float-c { animation: floatC 6.5s ease-in-out infinite 1.2s; }
        .tagline-enter { animation: fadeSlide 0.45s cubic-bezier(.22,1,.36,1) both; }
        .shimmer-orange { background: linear-gradient(90deg,#fb923c,#f59e0b,#fb923c); background-size:200% auto; -webkit-background-clip:text; -webkit-text-fill-color:transparent; animation: shimmerText 4s linear infinite; }
      `}</style>

      {/* Orb glows */}
      <div style={{ position:'absolute', top:'-10%', right:'-5%', width:480, height:480, background:'radial-gradient(circle, rgba(249,115,22,0.18) 0%, transparent 70%)', borderRadius:'50%', animation:'orb1 8s ease-in-out infinite', pointerEvents:'none' }} />
      <div style={{ position:'absolute', bottom:'-10%', left:'20%', width:360, height:360, background:'radial-gradient(circle, rgba(251,191,36,0.1) 0%, transparent 70%)', borderRadius:'50%', animation:'orb2 10s ease-in-out infinite', pointerEvents:'none' }} />
      {/* Grid */}
      <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)', backgroundSize:'44px 44px', pointerEvents:'none' }} />

      {/* ── Two-column layout ── */}
      <div className="relative z-10 flex flex-col justify-between w-full p-12">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 w-fit mb-0">
          <div className="p-2.5 rounded-xl" style={{ background:'rgba(249,115,22,0.15)', border:'0.5px solid rgba(249,115,22,0.3)' }}>
            <Wallet size={24} className="text-orange-400" />
          </div>
          <span className="text-xl font-bold text-white" style={{ fontFamily:'Outfit, sans-serif' }}>Budget Mantra</span>
        </Link>

        {/* Middle: headline left + cards right */}
        <div className="flex items-center gap-8 flex-1 py-12">

          {/* LEFT: copy */}
          <div className="flex-1 max-w-xs">
            <h2 className="text-4xl font-extrabold text-white leading-tight mb-4" style={{ fontFamily:'Outfit, sans-serif', letterSpacing:'-0.025em' }}>
              From your first<br/>payslip to your<br/>
              <span className="shimmer-orange">latest trip.</span>
            </h2>
            <p className="text-sm leading-relaxed mb-6" style={{ color:'rgba(255,255,255,0.42)' }}>
              One app to track everything that matters — salary, EMIs, goals, investments, and every rupee in between.
            </p>
            {/* Cycling tagline */}
            <div className="flex items-center gap-2 mb-8">
              <Sparkles size={12} className="text-orange-400 shrink-0" />
              <span key={lineIdx} className="tagline-enter text-xs font-semibold text-orange-300" style={{ fontFamily:'monospace' }}>
                {PUNCH_LINES[lineIdx]}
              </span>
            </div>
            {/* Feature bullets */}
            <div className="space-y-3">
              {[
                { icon: TrendingUp,    text: 'Track every rupee, in real-time', color: 'text-orange-400', bg: 'rgba(249,115,22,0.15)' },
                { icon: Shield,        text: 'Monitor your financial health',   color: 'text-emerald-400', bg: 'rgba(34,197,94,0.15)' },
                { icon: MessageSquare, text: 'Chanakya AI speaks Hinglish',     color: 'text-violet-400',  bg: 'rgba(139,92,246,0.15)' },
              ].map(({ icon: Icon, text, color, bg }) => (
                <div key={text} className="flex items-center gap-3" style={{ color:'rgba(255,255,255,0.7)' }}>
                  <div className="p-1.5 rounded-lg shrink-0" style={{ background: bg }}>
                    <Icon size={13} className={color} />
                  </div>
                  <span className="text-xs font-medium">{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: floating cards stacked with offset */}
          <div className="flex-1 flex flex-col items-center justify-center gap-4 relative" style={{ minHeight: 320 }}>
            {/* Card 1 */}
            <div className="float-a w-full max-w-[230px] flex items-center gap-3 rounded-2xl px-4 py-3.5"
              style={{ background:'rgba(255,255,255,0.06)', border:'0.5px solid rgba(255,255,255,0.12)', backdropFilter:'blur(12px)', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
              <div className="p-2 rounded-xl shrink-0" style={{ background:'rgba(249,115,22,0.2)' }}>
                <TrendingUp size={15} className="text-orange-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-white">Savings Rate</div>
                <div className="text-[10px]" style={{ color:'rgba(255,255,255,0.4)' }}>Track in real-time</div>
              </div>
              <span className="text-base font-extrabold text-orange-400 shrink-0" style={{ fontFamily:'Outfit, sans-serif' }}>53%</span>
            </div>

            {/* Card 2 — offset right */}
            <div className="float-b w-full max-w-[230px] flex items-center gap-3 rounded-2xl px-4 py-3.5 self-end"
              style={{ background:'rgba(255,255,255,0.06)', border:'0.5px solid rgba(34,197,94,0.2)', backdropFilter:'blur(12px)', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
              <div className="p-2 rounded-xl shrink-0" style={{ background:'rgba(34,197,94,0.2)' }}>
                <Target size={15} className="text-emerald-400" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-bold text-white">Goal reached! 🎉</div>
                <div className="text-[10px]" style={{ color:'rgba(255,255,255,0.4)' }}>Europe trip fund 100%</div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="float-c w-full max-w-[230px] flex items-center gap-3 rounded-2xl px-4 py-3.5"
              style={{ background:'rgba(255,255,255,0.06)', border:'0.5px solid rgba(249,115,22,0.25)', backdropFilter:'blur(12px)', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
              <div className="p-2 rounded-xl shrink-0" style={{ background:'rgba(249,115,22,0.2)' }}>
                <Zap size={15} className="text-orange-400" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-bold text-white">EMI due in 3 days</div>
                <div className="text-[10px] font-bold text-orange-400">HDFC Car Loan</div>
              </div>
            </div>

            {/* Card 4 — offset left */}
            <div className="float-b w-full max-w-[230px] flex items-center gap-3 rounded-2xl px-4 py-3.5 self-start"
              style={{ background:'rgba(255,255,255,0.06)', border:'0.5px solid rgba(139,92,246,0.2)', backdropFilter:'blur(12px)', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
              <div className="p-2 rounded-xl shrink-0" style={{ background:'rgba(139,92,246,0.2)' }}>
                <BarChart3 size={15} className="text-violet-400" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-bold text-white">Health Score</div>
                <div className="text-[10px] font-bold text-emerald-400">84 · Excellent</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-[10px]" style={{ color:'rgba(255,255,255,0.18)', borderTop:'0.5px solid rgba(255,255,255,0.06)', paddingTop:16 }}>
          © 2025 Budget Mantra · Made in India 🇮🇳
        </div>
      </div>
    </div>
  );
}

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, verifyLoginOtp, resendOtp } = useAuth();

  // form state
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // OTP step: null | { email, masked_email }
  const [otpPending, setOtpPending] = useState(null);
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const countdownRef = useRef(null);

  const containerRef = useRef(null);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setGoogleBtnWidth(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const startCountdown = () => {
    setCountdown(60);
    setCanResend(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(countdownRef.current); setCanResend(true); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (countdownRef.current) clearInterval(countdownRef.current); }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      await loginWithGoogle(credentialResponse.credential);
      toast.success('Welcome back!');
      navigate('/chatbot', { replace: true });
    } catch {
      toast.error('Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result?.pending) {
        setOtpPending({ email: result.email, masked_email: result.masked_email });
        setOtp('');
        startCountdown();
      } else {
        toast.success('Welcome back!');
        navigate('/chatbot');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Please enter the 6-digit code.');
    setLoading(true);
    try {
      await verifyLoginOtp(otpPending.email, otp);
      toast.success('Welcome back!');
      navigate('/chatbot', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    try {
      await resendOtp(otpPending.email);
      startCountdown();
      toast.success('OTP resent!');
    } catch {
      toast.error('Failed to resend OTP.');
    }
  };

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="login-page">
      <div className="grid lg:grid-cols-2 min-h-screen">
        <BrandingPanel />

        {/* Right Side - Form */}
        <div className="flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8">
              <Link to="/" className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg shadow-orange-500/25">
                  <Wallet size={24} className="text-white" />
                </div>
                <span className="text-xl font-bold text-stone-800 font-['Outfit']">Budget Mantra</span>
              </Link>
            </div>

            {/* OTP step */}
            {otpPending ? (
              <>
                <div className="mb-8">
                  <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-4">
                    <Mail size={26} className="text-orange-500" />
                  </div>
                  <h1 className="text-3xl font-bold text-stone-900 mb-2 font-['Outfit']">Check your email</h1>
                  <p className="text-stone-500 text-sm">
                    We sent a 6-digit code to <span className="font-semibold text-stone-700">{otpPending.masked_email}</span>
                  </p>
                  <p className="text-stone-400 text-xs mt-1">The code expires in 10 minutes.</p>
                </div>

                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-stone-700 font-medium">Verification Code</Label>
                    <Input
                      type="text" inputMode="numeric" placeholder="000000" maxLength={6}
                      value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="h-14 bg-white border-stone-200 focus:border-orange-400 focus:ring-orange-400/20 rounded-xl px-4 text-center text-2xl font-bold tracking-[0.4em]"
                      autoFocus required />
                  </div>

                  <Button type="submit" disabled={loading || otp.length !== 6}
                    className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all duration-300 disabled:opacity-50">
                    {loading
                      ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Verifying...</span>
                      : 'Verify & Sign In'
                    }
                  </Button>

                  <div className="text-center text-sm text-stone-400">
                    Didn't receive it?{' '}
                    {canResend
                      ? <button type="button" onClick={handleResendOtp} className="text-orange-600 hover:text-orange-700 font-medium">Resend OTP</button>
                      : <span>Resend in {countdown}s</span>
                    }
                  </div>

                  <button type="button" onClick={() => { setOtpPending(null); setOtp(''); }}
                    className="w-full text-center text-sm text-stone-400 hover:text-stone-600 transition-colors">
                    ← Back to login
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-stone-900 mb-2 font-['Outfit']">Login to your account</h1>
                  <p className="text-stone-500">
                    Don't have an account?{' '}
                    <Link to="/signup" className="text-orange-600 hover:text-orange-700 font-semibold" data-testid="signup-link">Sign up</Link>
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-stone-700 font-medium flex items-center gap-2">
                      <Mail size={16} className="text-stone-400" /> Email Address
                    </Label>
                    <Input id="email" type="email" data-testid="email-input" placeholder="you@example.com"
                      value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required autoComplete="email"
                      className="h-12 bg-white border-stone-200 focus:border-orange-400 focus:ring-orange-400/20 rounded-xl px-4" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-stone-700 font-medium flex items-center gap-2">
                      <Lock size={16} className="text-stone-400" /> Password
                    </Label>
                    <div className="relative">
                      <Input id="password" type={showPassword ? 'text' : 'password'} data-testid="password-input"
                        placeholder="Enter your password" value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required autoComplete="current-password"
                        className="h-12 bg-white border-stone-200 focus:border-orange-400 focus:ring-orange-400/20 rounded-xl px-4 pr-12" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-orange-500 transition-colors"
                        data-testid="toggle-password">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <Link to="/forgot-password" className="text-sm text-orange-600 hover:text-orange-700 font-medium">Forgot password?</Link>
                  </div>

                  <Button type="submit" disabled={loading} data-testid="login-button"
                    className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300">
                    {loading
                      ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Logging in...</span>
                      : 'Login'
                    }
                  </Button>
                </form>

                <div className="mt-6">
                  <div className="relative flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-stone-200" />
                    <span className="text-xs text-stone-400 font-medium">or continue with</span>
                    <div className="flex-1 h-px bg-stone-200" />
                  </div>
                  <div className="w-full flex justify-center overflow-hidden" ref={containerRef}>
                    {googleBtnWidth > 0 && (
                      <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => toast.error('Google sign-in failed')}
                        theme="outline" size="large" shape="rectangular" text="signin_with"
                        width={String(googleBtnWidth)} />
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-stone-200 text-center">
                  <Link to="/" className="text-stone-500 hover:text-orange-600 text-sm font-medium transition-colors" data-testid="back-home">← Back to Home</Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
